import { useRouter } from 'next/router';

import { useGetTreatment } from '@zg-rentals/split';

import accessUtils from '../utils/accessUtils';

import SplitTestConstants from '../constants/SplitTestConstants';
import { ROUTES } from '../constants/urls';

import { useMultiplexListingFlow } from './queries/multiplex';
import { useGetListingDetails } from './queries/properties';

import type { SplitExperimentsProps } from '@zg-rentals/split';

export const useAIListingCreation = () => {
  const router = useRouter();
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  // Only expose the split test on the single-family create-listing route (not
  // multiplex) so the experiment isn't over-counted for users who never enter
  // the flow. `enabled: false` skips the getTreatment call, so no impression fires.
  const isCreateListingRoute = router.pathname === ROUTES.CREATE_LISTING;

  const { data: splitExperiment, isLoading } = useGetTreatment(
    { featureName: SplitTestConstants.ZRM_AI_LISTING_CREATION },
    { enabled: isCreateListingRoute && !isInMultiplexListingFlow },
  );

  const splitExperiments: SplitExperimentsProps = splitExperiment
    ? { [SplitTestConstants.ZRM_AI_LISTING_CREATION]: splitExperiment }
    : {};

  return { splitExperiments, isLoading };
};

export const useCanAccessAIListingCreation = (): boolean => {
  const { splitExperiments } = useAIListingCreation();
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  if (isInMultiplexListingFlow) return false;

  const isFeed = listingDetails?.isFeed ?? false;
  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const lastActivatedMs = listingDetails?.lastActivatedMs;
  const canAccessRelistingFlow = Boolean(listingDetails?.canBeRelisted);
  const canAccessMLReactivationFlow = listingDetails
    ? accessUtils.canAccessMLReactivationFlow({ lastActivatedMs, isFeed, isMultifamily })
    : false;

  return accessUtils.canAccessAIListingCreation({
    splitExperiments,
    canAccessRelistingFlow,
    canAccessMLReactivationFlow,
    lastActivatedMs,
  });
};

export const useCanAccessAIListingCreationTestB = (): boolean => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);
  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  if (isInMultiplexListingFlow) return false;

  const isFeed = listingDetails?.isFeed ?? false;
  const isMultifamily = listingDetails?.isMultifamily ?? false;
  const lastActivatedMs = listingDetails?.lastActivatedMs;
  const canAccessRelistingFlow = Boolean(listingDetails?.canBeRelisted);
  const canAccessMLReactivationFlow = listingDetails
    ? accessUtils.canAccessMLReactivationFlow({ lastActivatedMs, isFeed, isMultifamily })
    : false;

  return accessUtils.canAccessAIListingCreationTestB({
    canAccessRelistingFlow,
    canAccessMLReactivationFlow,
    lastActivatedMs,
  });
};
